// components/pages/StatePage.js
// State page: the same filter-bar-driven views USHomePage has, scoped to one
// state. Topics come from createStateFeeds, so the Topic dropdown only ever
// offers All Categories / Legislative / Executive / Judicial / Updates.
//
//   All        -> every feed at once: one carousel per branch on mobile, a
//                 column per branch on desktop. Unchanged.
//   Branch     -> a single feed, so a vertical list rather than a carousel with
//                 nothing to scroll — the treatment Recents and Glance get on
//                 the home page.
//   Single feed-> the same list, reached from the Feed dropdown.
//
// Updates is an ordinary branch here; its feed reads from crawl.govglance.org
// rather than the main API, which useFeedData handles via crawlFeedAdapter.
//
// The active topic lives in the URL as ?topic=, not purely in local state. That
// is what lets the Updates carousel's View More reach this view: crawl feeds
// have no schema/table and so no /feed/:schema/:table route to open, but they
// can link back here with ?topic=updates, which lands on the narrowed list
// below and moves the filter bar's Topic control with it (StatePage feeds
// activeTopic to FeedFilterBar as currentCategory). It also means the view
// survives a refresh and can be shared.
//
// Section icons were removed here to match the home page, where icons are off
// everywhere (see useShowIcons in USHomePage).
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Spinner } from 'react-bootstrap';
import { ChevronRight } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

import MobileFeedCarousel from '../feed/MobileFeedCarousel';
import DeskCarousel from '../carousel/DeskCarousel';
import PersonalizedNewsReport from '../feed/PersonalizedNewsReport';
import FeedItem from '../feed/FeedItem';
import useFeedData from '../../hooks/useFeedData';
import IntegratedSearchNavBar from '../navigation/IntegratedSearchNavBar';
import { createStateFeeds, getStateInfo } from '../../utils/feedsConfig';
import '../feed/FeedItem.css';
import './StatePage.css';

// Branch order for the "All" view. These keys match both createStateFeeds and
// STATE_CATEGORY_LABELS in FeedFilterBar, so a topic picked in the filter bar
// maps straight onto one of them.
const STATE_BRANCHES = [
  { key: 'legislative', label: 'Legislative' },
  { key: 'executive', label: 'Executive' },
  { key: 'judicial', label: 'Judicial' },
  { key: 'updates', label: 'Updates' },
];

// The branches backed by our own database. Updates always has a feed, so it's
// excluded from the empty-state check — otherwise a state with no bills, no
// governor and no court opinions would stop reporting that.
const DB_BRANCHES = STATE_BRANCHES.filter(({ key }) => key !== 'updates');

// State feeds carry `apiParams` (no leading &); DeskCarousel expects a
// feed-shaped object with `apiEndpoint`, so convert on the way in.
//
// `viewAllLink` is what FeedSection falls back to when it can't build a
// /feed/:schema/:table URL — which is exactly the crawl case. Only the Updates
// branch passes one; everything else keeps the old null and resolves its own
// route from the endpoint.
const toCarouselSection = (feed, skip = 0, viewAllLink = null) => ({
  type: 'normal',
  id: `${feed.id}-${skip}`,
  title: feed.name,
  apiEndpoint: (feed.apiParams || '').replace(/skip=\d+/, `skip=${skip}`),
  feedName: feed.name,
  branch: feed.feedBranch,
  topic: feed.feedTopic,
  buttonName: feed.buttonName,
  viewAllLink,
});

// Generates multiple sections with incrementing skip values so DeskCarousel has
// something to rotate through (desktop only).
const transformFeedsForCarousel = (feeds, itemCount = 5, viewAllLink = null) => {
  if (!feeds || feeds.length === 0) return [];
  const result = [];
  feeds.forEach((feed) => {
    if (!feed.apiParams) return;
    for (let i = 0; i < itemCount; i++) result.push(toCarouselSection(feed, i, viewAllLink));
  });
  return result;
};

// Crawl-backed feeds have no schema/table, so there's no in-app feed page to
// open and the heading stays inert for those.
const feedRoute = (apiParams) => {
  const params = new URLSearchParams((apiParams || '').replace(/^[&?]/, ''));
  const schema = params.get('schema');
  const table = params.get('table');
  return { schema, table, path: schema && table ? `/feed/${schema}/${table}` : null };
};

// ── One feed: a vertical list of items, optionally under its own heading. Used
//    wherever the view has narrowed to a single feed, on both breakpoints.
//
//    showHeading is off when the narrowed view is showing exactly one feed: the
//    filter bar's Topic and Feed controls already name it, so a heading saying
//    the same thing a second time — "Web Crawl Updates" under a bar that reads
//    "Web Crawl Updates" — is pure repetition. With more than one feed stacked
//    the headings are what separates them, so they stay. ──
const StateFeedList = ({ feed, maxItems = 20, showHeading = true }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { data, loading, error } = useFeedData(feed.apiParams, maxItems);
  const { schema, table, path } = feedRoute(feed.apiParams);

  const handleHeadingClick = () => {
    if (!path) return;
    window.scrollTo(0, 0);
    navigate(path);
  };

  return (
    <div className="state-feed-block">
      {showHeading && (
        <div
          className="state-feed-heading"
          onClick={handleHeadingClick}
          style={{ cursor: path ? 'pointer' : 'default' }}
        >
          <span>{feed.name}</span>
          {path && <ChevronRight className="state-feed-chevron" />}
        </div>
      )}

      {loading && (!data || data.length === 0) && (
        <div className="d-flex justify-content-center py-4"><Spinner animation="grow" variant="light" /></div>
      )}

      {!loading && (error || !data || data.length === 0) && (
        <div className="state-feed-empty">
          <p>{error ? t('common:errorLoading', 'Error loading') : t('common:noContent', 'No content')}</p>
        </div>
      )}

      {!loading && data && data.map((item, idx) => (
        <div key={item.id || idx} className="mb-4">
          <FeedItem
            item={item}
            feedName={feed.name}
            branch={feed.feedBranch}
            topic={feed.feedTopic}
            buttonName={feed.buttonName || 'Document'}
            schema={schema}
            table={table}
            expanded={false}
          />
        </div>
      ))}
    </div>
  );
};

const StatePage = () => {
  const { t } = useTranslation('common');
  const { stateName } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const stateKey = (stateName || '').toLowerCase();
  const [isMobile, setIsMobile] = useState(false);

  // 'all' plus the branch keys. Mirrors activeTopic / activeFeed on the home
  // page: picking a topic narrows the view, picking a feed narrows further.
  // Seeded from the URL so a link carrying ?topic= opens on that view.
  const urlTopic = searchParams.get('topic') || 'all';
  const [activeTopic, setActiveTopic] = useState(urlTopic);
  const [activeFeed, setActiveFeed] = useState(null);

  // Where the Updates carousel sends View More. Built off the current path
  // rather than a hardcoded route so it follows whatever /:stateName resolves
  // to. Only the crawl branch uses it — see toCarouselSection.
  const updatesViewAllLink = `${location.pathname}?topic=updates`;

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Adopt a topic that arrived from the URL — a View More click, a refresh, a
  // shared link. Guarded on the values differing, so a change the filter bar
  // just made (which writes both the state and the param together) doesn't
  // bounce back through here and clear the feed it selected.
  useEffect(() => {
    if (urlTopic === activeTopic) return;
    setActiveTopic(urlTopic);
    setActiveFeed(null);
  }, [urlTopic, activeTopic]);

  // Switching states resets the view; the feeds behind the old selection no
  // longer exist. The param goes with it, otherwise a stale ?topic would be
  // re-adopted by the effect above on the next render.
  useEffect(() => {
    setActiveTopic('all');
    setActiveFeed(null);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateKey]);

  const currentState = getStateInfo(stateKey);
  const stateFeeds = useMemo(() => (stateKey ? createStateFeeds(stateKey) : null), [stateKey]);

  const findFeedById = useCallback((feedId) => {
    if (!feedId || !stateFeeds) return null;
    for (const { key } of STATE_BRANCHES) {
      const match = (stateFeeds[key] || []).find((f) => f.id === feedId);
      if (match) return match;
    }
    return null;
  }, [stateFeeds]);

  const handleFilterChange = useCallback(({ category, feed }) => {
    const nextTopic = category || 'all';
    setActiveTopic(nextTopic);
    setActiveFeed(feed && feed.id ? findFeedById(feed.id) : null);
    // Keep the URL honest about the view, and clear the param on the way back
    // to All Categories so it can't reassert itself later.
    setSearchParams(nextTopic === 'all' ? {} : { topic: nextTopic }, { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [findFeedById, setSearchParams]);

  // stateKey puts FeedFilterBar straight into state mode, so the Topic dropdown
  // reads from createStateFeeds instead of the federal feed config. Level,
  // Sort and Following are all federal-only concepts here.
  const feedFilterProps = useMemo(() => ({
    onFilterChange: handleFilterChange,
    currentSchema: 'united_states_of_america',
    stateKey,
    currentTable: '',
    currentFilterColumn: '',
    currentFilterString: '',
    currentCommittee: '',
    data: [],
    currentOrderBy: '',
    allowedOrderBy: [],
    onSortChange: () => {},
    currentCategory: activeTopic === 'all' ? null : activeTopic,
    hideSortButton: true,
    hideLevel: true,
    hideFollowing: true,
  }), [handleFilterChange, stateKey, activeTopic]);

  if (!currentState) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div>
          <Spinner id='spinner' animation="grow" variant="light" />
          <p style={{ color: 'var(--text-primary)' }}>
            {t('common:stateNotFound', 'State not found: {{stateName}}', { stateName })}
          </p>
        </div>
      </div>
    );
  }

  const activeBranchFeeds = activeTopic !== 'all' ? (stateFeeds?.[activeTopic] || []) : [];
  const hasAnyFeeds = DB_BRANCHES.some(({ key }) => stateFeeds?.[key]?.length > 0);

  // ── Narrowed to one feed (by topic or by the Feed dropdown): a list, in a
  //    centered column on desktop and full width on mobile. Shared by both
  //    breakpoints since the markup is identical. ──
  const narrowedFeeds = activeFeed ? [activeFeed] : activeBranchFeeds;
  const showFeedHeadings = narrowedFeeds.length > 1;

  if (activeFeed || activeTopic !== 'all') {
    const listBody = narrowedFeeds.length > 0
      ? narrowedFeeds.map((feed) => (
          <StateFeedList
            key={feed.id}
            feed={feed}
            maxItems={20}
            showHeading={showFeedHeadings}
          />
        ))
      : (
        <div className="empty-state">
          <p>{t('common:noFeedsAvailable', 'No feeds available for {{title}}', { title: currentState.fullName })}</p>
        </div>
      );

    if (isMobile) {
      return (
        <div id='homebg' style={{ paddingTop: 0 }} className="state-page-mobile">
          <IntegratedSearchNavBar currentCountry="us" feedFilterProps={feedFilterProps} />
          <div style={{ padding: '0 10px' }}>{listBody}</div>
          <style>{stateStyles}</style>
        </div>
      );
    }

    return (
      <div id='homebg' style={{ paddingTop: 0 }} className="state-page-root">
        <IntegratedSearchNavBar currentCountry="us" feedFilterProps={feedFilterProps} />
        <Container fluid className="state-list-wrap">
          <Row id="branchesRow">
            <Col md={12}>
              <div id="recentsCon" className="desktop-single-feed">
                <Col id="homeCol">
                  <div id="columnLefttPadding">{listBody}</div>
                </Col>
              </div>
            </Col>
          </Row>
        </Container>
        <style>{stateStyles}</style>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // ALL: every feed at once, original layout
  // ═══════════════════════════════════════════════════════════════

  if (isMobile) {
    return (
      <div id='homebg' style={{ paddingTop: 0 }} className="state-page-mobile">
        <IntegratedSearchNavBar currentCountry="us" feedFilterProps={feedFilterProps} />

        <PersonalizedNewsReport
          reportType="state"
          stateKey={stateKey}
          isFirstCarousel={false}
        />

        {STATE_BRANCHES.map(({ key, label }) => (
          stateFeeds?.[key]?.length > 0 && (
            <div className="state-branch-section" key={key}>
              <div className="branch-header">
                <div className="branch-title">{t(`common:section_${key}`, label)}</div>
              </div>
              {/* The Updates feeds are decorated with a viewAllLink so their
                  View all button has an in-app destination; MobileFeedCarousel
                  drops the button entirely for a crawl feed without one. */}
              <MobileFeedCarousel
                feeds={key === 'updates'
                  ? stateFeeds[key].map((f) => ({ ...f, viewAllLink: updatesViewAllLink }))
                  : stateFeeds[key]}
              />
            </div>
          )
        ))}

        {!hasAnyFeeds && (
          <div className="empty-state">
            <p>{t('common:noFeedsAvailable', 'No feeds available for {{title}}', { title: currentState.fullName })}</p>
            <p style={{ fontSize: '11px', opacity: 0.7 }}>
              {t('common:stateCodeLabel', 'State code: {{code}}', { code: currentState.code })}
            </p>
          </div>
        )}

        <style>{stateStyles}</style>
      </div>
    );
  }

  return (
    <div id='homebg' style={{ paddingTop: 0 }} className="state-page-root">
      <IntegratedSearchNavBar currentCountry="us" feedFilterProps={feedFilterProps} />
      <Container fluid>
        <Row id="branchesRow">
          {STATE_BRANCHES.map(({ key, label }) => (
            <Col md={4} key={key}>
              {stateFeeds?.[key]?.length > 0 ? (
                <DeskCarousel
                  title={t(`common:section_${key}`, label)}
                  sections={transformFeedsForCarousel(
                    stateFeeds[key],
                    5,
                    key === 'updates' ? updatesViewAllLink : null
                  )}
                  viewAllLink={null}
                  intervalDuration={30000}
                  disableNavigation={true}
                />
              ) : (
                <div className="branch-section">
                  <h5 className="branch-title">{t(`common:section_${key}`, label)}</h5>
                  <div style={{ padding: '20px', borderRadius: '8px' }}>
                    <p>
                      {t('common:noFeedsFoundForBranch', 'No {{branch}} feeds found', {
                        branch: t(`common:section_${key}`, label).toLowerCase(),
                      })}
                    </p>
                    <p style={{ fontSize: '11px', opacity: 0.7 }}>
                      {t('common:expectedApiLabel', 'Expected API: {{api}}', {
                        api: key === 'legislative'
                          ? `${currentState.code}_bills`
                          : key === 'executive'
                            ? currentState.tableName
                            : key === 'updates'
                              ? `crawl: state=${currentState.code}`
                              : `supreme_${currentState.courtCode}`,
                      })}
                    </p>
                  </div>
                </div>
              )}
            </Col>
          ))}
        </Row>
      </Container>
      <style>{stateStyles}</style>
    </div>
  );
};

// Single-feed list views match the home page's Glance / Recents column: narrow
// and centered on desktop, full width on mobile.
const stateStyles = `
  .state-list-wrap { max-width: 700px; margin-left: auto; margin-right: auto; }
  .state-feed-block { margin-bottom: 8px; }
  .state-feed-heading {
    display: flex; align-items: center; gap: 6px;
    padding: 8px 10px; font-size: 17px; font-weight: bold;
    color: var(--text-primary);
  }
  .state-feed-chevron { width: 16px; height: 16px; }
  .state-feed-empty { padding: 12px 10px 20px; }
  .state-feed-empty p { color: var(--text-tertiary); font-size: 14px; margin: 0; }
  @media screen and (min-width: 768px) {
    .state-feed-heading {
      font-size: 18px; padding: 0 0 8px;
      margin-bottom: 14px;
      border-bottom: 1px solid var(--border-color, rgba(128,128,128,0.2));
    }
    .state-feed-block { margin-bottom: 28px; }
  }
  [data-theme="dark"] .state-feed-heading { color: #ffffff; }
  [data-theme="light"] .state-feed-heading { color: #000000; }
`;

export default StatePage;